// ═══════════════════════════════════════════════════════════
//  useCloudSync — bidirectional Firestore ↔ localStorage sync
//
//  Behaviour:
//    • If Firebase is configured: real-time listener keeps all
//      browser tabs / devices in sync via Firestore.
//    • If Firebase is NOT configured (placeholder .env values):
//      falls back silently to localStorage only.
//    • Merges remote + local on first load (takes the higher
//      visit count so counts never go backwards).
// ═══════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef } from "react";
import {
  db, isConfigured, SHARED_DOC,
  onSnapshot, setDoc, getDoc, serverTimestamp,
} from "./firebase";

const VISITS_KEY   = "spacemancraft_visits";
const CONSTANTS_KEY = "spacemancraft_constants";

// Merge two visits objects — keep the higher count per page
function mergeVisits(local, remote) {
  const merged = { ...local };
  for (const [id, count] of Object.entries(remote || {})) {
    merged[id] = Math.max(merged[id] || 0, count);
  }
  return merged;
}

export function useCloudSync() {
  const [visits, setVisits]           = useState(
    () => JSON.parse(localStorage.getItem(VISITS_KEY)    || "{}")
  );
  const [customConstants, setCustomConstants] = useState(
    () => JSON.parse(localStorage.getItem(CONSTANTS_KEY) || "{}")
  );
  const [syncStatus, setSyncStatus]   = useState(
    isConfigured ? "connecting" : "offline"
  );

  // Ref to avoid re-subscribing when values change
  const visitsRef          = useRef(visits);
  const customConstantsRef = useRef(customConstants);
  visitsRef.current          = visits;
  customConstantsRef.current = customConstants;

  // ── Write to Firestore (debounced by caller) ─────────────
  const pushToCloud = useCallback(async (nextVisits, nextConstants) => {
    if (!isConfigured) return;
    try {
      await setDoc(SHARED_DOC(), {
        visits:    nextVisits    ?? visitsRef.current,
        constants: nextConstants ?? customConstantsRef.current,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.warn("Firestore write failed:", e.message);
      setSyncStatus("error");
    }
  }, []);

  // ── Subscribe to real-time updates ───────────────────────
  useEffect(() => {
    if (!isConfigured) return;

    const docRef = SHARED_DOC();

    const unsub = onSnapshot(docRef,
      (snap) => {
        if (!snap.exists()) {
          // First time — push local data up
          pushToCloud(visitsRef.current, customConstantsRef.current);
          setSyncStatus("synced");
          return;
        }

        const remote = snap.data();
        setSyncStatus("synced");

        // Merge visits — take highest count per page
        const merged = mergeVisits(visitsRef.current, remote.visits || {});
        setVisits(merged);
        localStorage.setItem(VISITS_KEY, JSON.stringify(merged));

        // Constants — remote wins (last write wins)
        if (remote.constants && Object.keys(remote.constants).length > 0) {
          setCustomConstants(remote.constants);
          localStorage.setItem(CONSTANTS_KEY, JSON.stringify(remote.constants));
        }
      },
      (err) => {
        console.warn("Firestore listener error:", err.message);
        setSyncStatus("error");
      }
    );

    return () => unsub();
  }, [pushToCloud]);

  // ── Increment a page visit count ─────────────────────────
  const recordVisit = useCallback((pageId) => {
    setVisits(prev => {
      const next = { ...prev, [pageId]: (prev[pageId] || 0) + 1 };
      localStorage.setItem(VISITS_KEY, JSON.stringify(next));
      pushToCloud(next, null);
      return next;
    });
  }, [pushToCloud]);

  // ── Save a constants override ─────────────────────────────
  const saveConstants = useCallback((overrides) => {
    setCustomConstants(overrides);
    localStorage.setItem(CONSTANTS_KEY, JSON.stringify(overrides));
    pushToCloud(null, overrides);
  }, [pushToCloud]);

  return { visits, customConstants, recordVisit, saveConstants, syncStatus };
}
